import { useCallback, useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { CallIntelligenceOrgWetags } from '@wetags/call-intelligence';
import { hasSchemaACL, isWeaveUser } from '@frontend/auth-helpers';
import { useCallIntelSubscription } from '@frontend/call-intel-core';
import { pendo } from '@frontend/tracking';
import { useEligibility, type EligibilityCriteria } from '@frontend/user-preferences';
import { getCallIntelTrialTeaserTrackingIds } from '../tracking-ids';

const ADMIN_TEASER_CRITERIA: EligibilityCriteria = {
  type: 'dismiss-cooldown-sequence',
  cooldownDays: [14, 60, 60, 180],
};

const USER_TEASER_CRITERIA: EligibilityCriteria = {
  type: 'dismiss-cooldown',
  cooldownDays: 14,
  maxDismisses: 2,
};

type UseCallInsightTrialTeaserOptions = {
  locationId: string;
  isDemo?: boolean;
};

export const useCallInsightTrialTeaser = ({ locationId, isDemo = false }: UseCallInsightTrialTeaserOptions) => {
  const [isWetagEnabled] = CallIntelligenceOrgWetags.useWetag('call-intel-insights-trial-teaser');

  const { isCallIntelFreeTrialEligible } = useCallIntelSubscription({ locationIds: [locationId] });

  const isAdmin = useMemo(
    () => isWeaveUser() || hasSchemaACL(locationId, Permission.MANAGE_ADD_ON_SUBSCRIPTIONS),
    [locationId]
  );

  const { shouldShow, recordDismiss } = useEligibility({
    featureId: isAdmin ? 'call-intel-insights-trial-teaser-admin' : 'call-intel-insights-trial-teaser-user',
    defaultCriteria: isAdmin ? ADMIN_TEASER_CRITERIA : USER_TEASER_CRITERIA,
  });

  const isEligible = !isDemo && isCallIntelFreeTrialEligible && Boolean(isWetagEnabled) && shouldShow;

  const onDismiss = useCallback(() => {
    recordDismiss();
  }, [recordDismiss]);

  const registerTeaserViewed = useCallback(() => {
    pendo.track(getCallIntelTrialTeaserTrackingIds(isAdmin).view, { locationId });
  }, [isAdmin, locationId]);

  return {
    isEligible,
    isAdmin,
    onDismiss,
    registerTeaserViewed,
  };
};
